import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth token from localStorage
const getAuthToken = () => {
    return localStorage.getItem('access_token');
};

// Axios instance
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
});

// Add auth token to requests
axiosInstance.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Action Types
export const FETCH_MAIN_PRIVILEGES_REQUEST = 'FETCH_MAIN_PRIVILEGES_REQUEST';
export const FETCH_MAIN_PRIVILEGES_SUCCESS = 'FETCH_MAIN_PRIVILEGES_SUCCESS';
export const FETCH_MAIN_PRIVILEGES_FAILURE = 'FETCH_MAIN_PRIVILEGES_FAILURE';

export const FETCH_PRIVILEGE_DETAILS_REQUEST = 'FETCH_PRIVILEGE_DETAILS_REQUEST';
export const FETCH_PRIVILEGE_DETAILS_SUCCESS = 'FETCH_PRIVILEGE_DETAILS_SUCCESS';
export const FETCH_PRIVILEGE_DETAILS_FAILURE = 'FETCH_PRIVILEGE_DETAILS_FAILURE';

export const ADD_PRIVILEGE_REQUEST = 'ADD_PRIVILEGE_REQUEST';
export const ADD_PRIVILEGE_SUCCESS = 'ADD_PRIVILEGE_SUCCESS';
export const ADD_PRIVILEGE_FAILURE = 'ADD_PRIVILEGE_FAILURE';

export const UPDATE_PRIVILEGE_REQUEST = 'UPDATE_PRIVILEGE_REQUEST';
export const UPDATE_PRIVILEGE_SUCCESS = 'UPDATE_PRIVILEGE_SUCCESS';
export const UPDATE_PRIVILEGE_FAILURE = 'UPDATE_PRIVILEGE_FAILURE';

export const DELETE_PRIVILEGE_REQUEST = 'DELETE_PRIVILEGE_REQUEST';
export const DELETE_PRIVILEGE_SUCCESS = 'DELETE_PRIVILEGE_SUCCESS';
export const DELETE_PRIVILEGE_FAILURE = 'DELETE_PRIVILEGE_FAILURE';

// Action Creators

/**
 * Fetch all main privileges (privileges without a parent)
 */
export const fetchMainPrivileges = () => {
    return async (dispatch) => {
        dispatch({ type: FETCH_MAIN_PRIVILEGES_REQUEST });
        try {
            console.log('Fetching from URL:', `${API_BASE_URL}/privileges/`);
            const response = await axiosInstance.get('/privileges/');
            console.log('API Response:', response);
            console.log('Response data:', response.data);
            
            // Handle paginated response - extract results array
            const privileges = response.data.results || response.data;
            console.log('Extracted privileges:', privileges);
            console.log('Privileges is array:', Array.isArray(privileges));
            console.log('Privileges count:', privileges?.length);
            
            dispatch({
                type: FETCH_MAIN_PRIVILEGES_SUCCESS,
                payload: privileges
            });
            return privileges;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 
                                 error.response?.data?.detail ||
                                 error.message;
            dispatch({
                type: FETCH_MAIN_PRIVILEGES_FAILURE,
                payload: errorMessage
            });
            throw error;
        }
    };
};

/**
 * Fetch details of a specific privilege (including sub-privileges and internal privileges)
 * @param {number} privilegeId - The ID of the privilege
 */
export const fetchPrivilegeDetails = (privilegeId) => {
    return async (dispatch) => {
        dispatch({ type: FETCH_PRIVILEGE_DETAILS_REQUEST });
        try {
            const response = await axiosInstance.get(`/privileges/${privilegeId}/`);
            dispatch({
                type: FETCH_PRIVILEGE_DETAILS_SUCCESS,
                payload: response.data
            });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 
                                 error.response?.data?.detail ||
                                 error.message;
            dispatch({
                type: FETCH_PRIVILEGE_DETAILS_FAILURE,
                payload: errorMessage
            });
            throw error;
        }
    };
};

/**
 * Add a new privilege (main, sub, or internal)
 * @param {Object} privilegeData - The privilege data
 * @param {string} privilegeData.name - Name of the privilege
 * @param {string} privilegeData.description - Description (optional)
 * @param {string} privilegeData.pathway - Frontend route/path (optional)
 * @param {number} privilegeData.main_privilege - Parent privilege ID (for sub-privileges)
 * @param {Array<number>} privilegeData.internal_privileges - Array of internal privilege IDs
 */
export const addPrivilege = (privilegeData) => {
    return async (dispatch) => {
        dispatch({ type: ADD_PRIVILEGE_REQUEST });
        try {
            console.log('Adding privilege with data:', privilegeData);
            console.log('Auth token exists:', !!getAuthToken());
            
            const response = await axiosInstance.post(
                '/add-privileges/',
                privilegeData
            );
            
            console.log('Privilege added successfully:', response.data);
            dispatch({
                type: ADD_PRIVILEGE_SUCCESS,
                payload: response.data
            });
            return response.data;
        } catch (error) {
            console.error('Error adding privilege:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            
            const errorMessage = error.response?.data?.message || 
                                 error.response?.data?.detail ||
                                 JSON.stringify(error.response?.data) ||
                                 error.message;
            dispatch({
                type: ADD_PRIVILEGE_FAILURE,
                payload: errorMessage
            });
            throw error;
        }
    };
};

/**
 * Update an existing privilege
 * @param {number} privilegeId - The ID of the privilege
 * @param {Object} updateData - The data to update
 */
export const updatePrivilege = (privilegeId, updateData) => {
    return async (dispatch) => {
        dispatch({ type: UPDATE_PRIVILEGE_REQUEST });
        try {
            const response = await axiosInstance.patch(
                `/privileges/${privilegeId}/`,
                updateData
            );
            dispatch({
                type: UPDATE_PRIVILEGE_SUCCESS,
                payload: response.data
            });
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 
                                 error.response?.data?.detail ||
                                 JSON.stringify(error.response?.data) ||
                                 error.message;
            dispatch({
                type: UPDATE_PRIVILEGE_FAILURE,
                payload: errorMessage
            });
            throw error;
        }
    };
};

/**
 * Delete a privilege
 * @param {number} privilegeId - The ID of the privilege to delete
 */
export const deletePrivilege = (privilegeId) => {
    return async (dispatch) => {
        dispatch({ type: DELETE_PRIVILEGE_REQUEST });
        try {
            await axiosInstance.delete(`/privileges/${privilegeId}/`);
            dispatch({
                type: DELETE_PRIVILEGE_SUCCESS,
                payload: privilegeId
            });
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 
                                 error.response?.data?.detail ||
                                 error.message;
            dispatch({
                type: DELETE_PRIVILEGE_FAILURE,
                payload: errorMessage
            });
            throw error;
        }
    };
};
